import React, { useState } from "react";
import {
  Box,
  Button,
  ButtonDropdown,
  ButtonDropdownProps,
  FormField,
  Input,
  Modal,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { Investigation, Stage } from "../../lusca-search-api/generated-src";
import {
  InvestigationApiError,
  useSetInvestigationReminder,
} from "../../hooks/useInvestigations";
import { reminderLabel, reminderTime } from "./RemindMeButton";
import { saveErrorText } from "./versionConflict";

const HOUR_MS = 60 * 60 * 1000;
/** The Notifications service schedules 5 minutes to 7 days ahead. */
const MAX_HOURS = 7 * 24;

/** My pending reminder on the case, or null (none, failed, or already past). */
export const myActiveReminder = (investigation: Investigation): Date | null => {
  const reminder = investigation.myReminder;
  if (!reminder?.remindAt || reminder.status === "FAILED") return null;
  const at = new Date(reminder.remindAt);
  return at.getTime() > Date.now() ? at : null;
};

/** Hours typed in the custom dialog: a number from 0.1 up to 7 days, else null. */
export const parseHours = (value: string): number | null => {
  const hours = Number(value.trim());
  if (!value.trim() || !Number.isFinite(hours)) return null;
  return hours >= 0.1 && hours <= MAX_HOURS ? hours : null;
};

interface InvestigationReminderButtonProps {
  stage: Stage;
  investigation: Investigation;
  onError: (message: string) => void;
}

/**
 * "Remind me" in the case header: a private email + Slack reminder to check back on the case.
 * Anyone signed in may set one; only they see it. Picking again replaces it.
 */
export function InvestigationReminderButton({
  stage,
  investigation,
  onError,
}: InvestigationReminderButtonProps) {
  const setReminder = useSetInvestigationReminder(stage);
  const [customOpen, setCustomOpen] = useState(false);
  const [hours, setHours] = useState("");
  const active = myActiveReminder(investigation);

  const save = (at: Date | null): void => {
    setReminder
      .mutateAsync({
        investigationId: investigation.id,
        remindAt: at ? at.toISOString() : null,
      })
      .catch((err: InvestigationApiError) => onError(saveErrorText(err)));
  };

  const items: ButtonDropdownProps.ItemOrGroup[] = [
    { id: "in-1h", text: "In 1 hour" },
    { id: "in-4h", text: "In 4 hours" },
    { id: "tomorrow", text: "Tomorrow at 09:00" },
    { id: "custom", text: "In… hours" },
    ...(active ? [{ id: "cancel", text: "Cancel reminder" }] : []),
  ];

  const onItemClick = (id: string): void => {
    if (id === "custom") {
      setHours("");
      setCustomOpen(true);
    } else if (id === "cancel") {
      save(null);
    } else if (id === "in-4h") {
      save(new Date(Date.now() + 4 * HOUR_MS));
    } else {
      save(reminderTime(id as "in-1h" | "tomorrow"));
    }
  };

  const parsed = parseHours(hours);
  return (
    <>
      <ButtonDropdown
        data-testid="case-remind-me"
        loading={setReminder.isLoading}
        items={items}
        ariaLabel={
          active
            ? `Reminder set for ${reminderLabel(active)}; change it`
            : "Remind me to check back on this investigation"
        }
        onItemClick={({ detail }) => onItemClick(detail.id)}
      >
        {active ? `Reminder ${reminderLabel(active)}` : "Remind me"}
      </ButtonDropdown>
      <Modal
        visible={customOpen}
        onDismiss={() => setCustomOpen(false)}
        header="Remind me"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setCustomOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={parsed === null}
                onClick={() => {
                  if (parsed === null) return;
                  setCustomOpen(false);
                  save(new Date(Date.now() + parsed * HOUR_MS));
                }}
              >
                Set reminder
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <FormField
          label="In how many hours?"
          errorText={
            hours.trim() && parsed === null
              ? `Enter 0.1 to ${MAX_HOURS} hours`
              : undefined
          }
        >
          <Input
            type="number"
            inputMode="decimal"
            value={hours}
            autoFocus
            onChange={({ detail }) => setHours(detail.value)}
          />
        </FormField>
      </Modal>
    </>
  );
}
